import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSelector } from "./LanguageSelector";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { t, path } = useLang();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => setSettings(null));
  }, []);

  const navLinks = [
    { to: "/odf-technology", key: "odf-technology", label: t.nav.technology },
    { to: "/solutions", key: "solutions", label: t.nav.solutions },
    { to: "/partnerships", key: "partnerships", label: t.nav.partnerships },
    { to: "/about", key: "about", label: t.nav.about },
    { to: "/contact", key: "contact", label: t.nav.contact },
  ];

  return (
    <footer className="bg-[#061A26] border-t border-white/10" data-testid="site-footer">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <Logo />
            <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-sm" data-testid="footer-description">
              {t.footer.desc}
            </p>
            <div className="mt-6 space-y-2 text-sm text-slate-400">
              {settings?.email && (
                <p className="flex items-center gap-2" data-testid="footer-email"><Mail size={14} className="text-[#19C7C1]" />{settings.email}</p>
              )}
              {settings?.phone && (
                <p className="flex items-center gap-2" data-testid="footer-phone"><Phone size={14} className="text-[#19C7C1]" />{settings.phone}</p>
              )}
              {settings?.address && (
                <p className="flex items-center gap-2" data-testid="footer-address"><MapPin size={14} className="text-[#19C7C1]" />{settings.address}</p>
              )}
            </div>
            {settings?.linkedin && (
              <a
                href={settings.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.a11y.linkedinLabel}
                data-testid="footer-linkedin"
                className="mt-6 inline-flex items-center justify-center w-10 h-10 border border-white/15 rounded-full text-slate-400 hover:text-[#19C7C1] hover:border-[#19C7C1]/50 transition-colors duration-300"
              >
                <Linkedin size={16} />
              </a>
            )}
          </div>

          <div className="md:col-span-3">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-white/50 mb-5">{t.footer.nav}</h3>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.key}>
                  <Link to={path(l.to)} data-testid={`footer-nav-${l.key}`} className="text-sm text-slate-300 hover:text-[#19C7C1] transition-colors duration-300">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-white/50 mb-5">{t.footer.legal}</h3>
            <ul className="space-y-3">
              <li><Link to={path("/privacy")} data-testid="footer-privacy-link" className="text-sm text-slate-300 hover:text-[#19C7C1] transition-colors duration-300">{t.footer.privacy}</Link></li>
              <li><Link to={path("/terms")} data-testid="footer-terms-link" className="text-sm text-slate-300 hover:text-[#19C7C1] transition-colors duration-300">{t.footer.terms}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-white/50 mb-5">{t.footer.language}</h3>
            <LanguageSelector direction="up" />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="footer-disclaimer">
            {t.footer.disclaimer}
          </p>
          <p className="mt-4 text-xs text-slate-600">
            © {new Date().getFullYear()} NORTIS. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};
